import React, { useState, useEffect } from 'react';
import { 
  Bot, ShieldCheck, Zap, ArrowRight, Brain, 
  Target, Activity, TrendingUp, Sun, Cloud, 
  CloudLightning, Loader2, CheckCircle2, AlertTriangle,
  ClipboardList
} from 'lucide-react';
import { StorageService } from '../services/storageService';
import { GeminiService } from '../services/geminiService';
import { BackendQuestion, User } from '../types';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { PageLoader } from './PageLoader';

const FALLBACK_DIAG_QUESTIONS: BackendQuestion[] = [
  {
    _id: "diag_fall_1",
    text: "A resident refuses to take a bath, stating she took one yesterday. What is the most appropriate initial action by the nursing assistant?",
    type: "multiple-choice",
    category: "Principles & Rights",
    difficulty: "Medium",
    options: [
      { _id: "opt_1_a", text: "Force her into the shower as it is scheduled on her medical care plan.", isCorrect: false },
      { _id: "opt_1_b", text: "Respect her refusal, document it, and offer to assist with washing her hands, face, and oral care instead.", isCorrect: true },
      { _id: "opt_1_c", text: "Notify her family immediately that she is refusing hygiene care.", isCorrect: false },
      { _id: "opt_1_d", text: "Inform her that she will not be allowed to attend meals until she bathes.", isCorrect: false }
    ]
  },
  {
    _id: "diag_fall_2",
    text: "While assisting a resident to the dining room, they suddenly lose their balance and begin to suffer a fall. What should the nursing assistant do?",
    type: "multiple-choice",
    category: "Safety & Prevention",
    difficulty: "Medium",
    options: [
      { _id: "opt_2_a", text: "Try to hold the resident completely upright to prevent them from hitting the ground.", isCorrect: false },
      { _id: "opt_2_b", text: "Step away and call for immediate nursing help.", isCorrect: false },
      { _id: "opt_2_c", text: "Keep a wide stance, pull the resident close to your body, and gently slide them down your leg to the floor.", isCorrect: true },
      { _id: "opt_2_d", text: "Grab the resident's arms and pull them back up quickly to prevent knee damage.", isCorrect: false }
    ]
  },
  {
    _id: "diag_fall_3",
    text: "Which of the following is the single most effective method to prevent the spread of pathogens in a healthcare environment?",
    type: "multiple-choice",
    category: "Safety & Prevention",
    difficulty: "Medium",
    options: [
      { _id: "opt_3_a", text: "Wearing double layers of gloves at all times for patient contact.", isCorrect: false },
      { _id: "opt_3_b", text: "Using high-strength chemical air purifiers in hallways and bedrooms.", isCorrect: false },
      { _id: "opt_3_c", text: "Performing thorough hand hygiene with soap and water or alcohol rub before and after client contact.", isCorrect: true },
      { _id: "opt_3_d", text: "Sprinkling antiseptic powder on clean bed sheets daily.", isCorrect: false }
    ]
  },
  {
    _id: "diag_fall_4",
    text: "While measuring vital signs, you find a diabetic resident's blood pressure is 178/102 mmHg. What is your best action?",
    type: "multiple-choice",
    category: "Vitals & Metrics",
    difficulty: "Medium",
    options: [
      { _id: "opt_4_a", text: "Wait one hour and measure it again to confirm accuracy.", isCorrect: false },
      { _id: "opt_4_b", text: "Instruct the resident to lie flat and take deep breaths for 15 minutes.", isCorrect: false },
      { _id: "opt_4_c", text: "Report this hypertensive measurement to the supervising nurse immediately.", isCorrect: true },
      { _id: "opt_4_d", text: "Tell the resident their pressure is dangerously high to keep them informed.", isCorrect: false }
    ]
  },
  {
    _id: "diag_fall_5",
    text: "When assisting in feeding a resident who has left-sided weakness (hemiplegia) due to a stroke, food should be placed:",
    type: "multiple-choice",
    category: "Personal ADL Care",
    difficulty: "Medium",
    options: [
      { _id: "opt_5_a", text: "In the stronger, unaffected right side of the mouth to facilitate mastication.", isCorrect: true },
      { _id: "opt_5_b", text: "In the weaker, affected left side of the mouth to stimulate muscle action.", isCorrect: false },
      { _id: "opt_5_c", text: "Deep in the throat close to the pharynx to trigger swallowing reflexes.", isCorrect: false },
      { _id: "opt_5_d", text: "Mixed with fluids and given via physical suction straws.", isCorrect: false }
    ]
  }
];

interface DiagnosticLaunchPadProps {
  user: User;
  certName: string;
  onComplete: (forecast: any) => void;
}

export const DiagnosticLaunchPad: React.FC<DiagnosticLaunchPadProps> = ({ user, certName, onComplete }) => {
  const tokens = useThemeTokens();
  const [step, setStep] = useState<'intro' | 'registry' | 'quiz' | 'analyzing' | 'results'>('intro');
  const [registry, setRegistry] = useState<string | null>(null);
  const [questions, setQuestions] = useState<BackendQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<{question: string, category: string, isCorrect: boolean}[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [currentSequence, setCurrentSequence] = useState<string[]>([]);

  useEffect(() => {
    if (step === 'quiz' && questions.length === 0) {
      loadDiagnosticQuestions();
    }
  }, [step]);

  const loadDiagnosticQuestions = async () => {
    setIsLoadingQuestions(true);
    try {
      // Fetch board-standard questions
      const diff = registry === 'Prometric' ? 'Hard' : 'Medium';
      const res = await StorageService.getQuestions(user.activeCertId || 'cna', { limit: 5, difficulty: diff });
      if (res && res.items && res.items.length > 0) {
        setQuestions(res.items);
      } else {
        console.warn("Got empty questions. Loading calibrated fallback questions.");
        setQuestions(FALLBACK_DIAG_QUESTIONS);
      }
    } catch (err) {
      console.error("Failed to load diagnostic items, using fallback questions:", err);
      setQuestions(FALLBACK_DIAG_QUESTIONS);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleAnswer = (optionId: string) => {
    if (isAnswered) return;
    
    const q = questions[currentIndex];
    // Robust lookup: search by _id then fallback to index string match
    const option = q.options?.find((o, idx) => o._id === optionId || idx.toString() === optionId);
    
    if (q.type === 'ordering') {
      if (currentSequence.includes(optionId)) return;
      const nextSequence = [...currentSequence, optionId];
      setCurrentSequence(nextSequence);
      if (nextSequence.length === (q.options?.length || 0)) {
        setIsAnswered(true);
        // Correct sequence is usually the order they come in the questions array
        const correctSequence = q.options?.map((o, idx) => o._id || idx.toString()).join(',');
        const isMatch = nextSequence.join(',') === correctSequence;
        processResult(isMatch);
      }
    } else {
      setIsAnswered(true);
      setSelectedOptionId(optionId);
      
      // Liberal boolean check to catch strings or numbers
      const isCorrectValue = option?.isCorrect === true || 
                            String(option?.isCorrect).toLowerCase() === 'true' || 
                            Number(option?.isCorrect) === 1;
      
      processResult(Boolean(isCorrectValue));
    }
  };

  const processResult = (isCorrect: boolean) => {
    const q = questions[currentIndex];
    const newAnswers = [...answers, {
      question: q.text,
      category: q.category,
      isCorrect: isCorrect
    }];
    setAnswers(newAnswers);

    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setIsAnswered(false);
        setSelectedOptionId(null);
        setCurrentSequence([]);
      } else {
        setStep('analyzing');
        const correctCount = newAnswers.filter(a => a.isCorrect).length;
        const totalCount = Math.max(questions.length, 1); 
        const finalScore = Math.round((correctCount / totalCount) * 100);
        
        console.log(`[Diagnostic] Session Complete. Correct: ${correctCount}/${totalCount}. Score: ${finalScore}%`);
        generateReport(newAnswers, finalScore);
      }
    }, 1200);
  };
 
  const generateReport = async (finalAnswers?: any[], rawScore?: number) => {
    try {
      const answersToUse = finalAnswers || answers;
      // Use rawScore if provided (even if 0), otherwise recalculate
      const scoreToUse = (rawScore !== undefined)
        ? rawScore 
        : Math.round((answersToUse.filter(a => a.isCorrect).length / Math.max(questions.length, 1)) * 100);
      
      console.log(`[Diagnostic] Generating report. Score: ${scoreToUse}%`);
      
      // Track completion
      StorageService.trackUserEvent({ 
        action: 'diagnostic_completed', 
        category: 'onboarding', 
        value: scoreToUse, 
        metadata: { score: scoreToUse, registry: registry || 'General' } 
      });

      const res = await GeminiService.generateDiagnosticReport(answersToUse, certName, registry || undefined, scoreToUse);
      
      // Ensure the AI report respects the actual score calculated
      const finalReport = {
        ...res,
        readinessScore: scoreToUse
      };
      
      setReport(finalReport);
      setStep('results');
    } catch (err) {
      console.error("AI Diagnostic failed", err);
      // Fallback if AI fails
      setReport({
        weatherStatus: 'clouds',
        readinessScore: 70,
        diagnosticAudit: `Assessment complete for your ${registry} baseline. You have a solid foundation but need to focus on specific clinical safety protocols.`,
        domainMastery: [
          { domain: 'Clinical Logic', score: 75, status: 'Stable' },
          { domain: 'Patient Safety', score: 65, status: 'At Risk' }
        ],
        learningPath: ["Complete Safety Lab", "Practice Infection Control", "Review Communication Ethics"]
      });
      setStep('results');
    }
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await onComplete({ ...report, registry });
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (step === 'intro') {
    return (
      <div 
        className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-md flex justify-center items-start pt-4 md:pt-12 p-4 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="diagnostic-intro-title"
      >
        <div className="max-w-xl w-full bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-500">
          <div className={`${tokens.heroBannerBg} p-8 text-center text-white relative overflow-hidden`}>
             <div className="absolute top-0 right-0 p-4 opacity-10">
                <ShieldCheck size={100} aria-hidden="true" />
             </div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 rounded-full mb-4">
                  <span className="text-[10px] font-black tracking-[0.2em] text-white">Clinical Readiness Check</span>
                </div>
                <h1 id="diagnostic-intro-title" className={`text-3xl md:text-4xl ${tokens.headerFont || 'font-black'} mb-3`}>Welcome to <span className={tokens.accentText}>Study Hub</span></h1>
                <p className="text-white/80 text-sm font-medium max-w-sm mx-auto leading-relaxed">
                   Let's get you ready for your exam. Answer 5 quick questions so we can build your personalized study plan.
                </p>
              </div>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid grid-cols-2 gap-4">
               <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl text-center border border-slate-100 dark:border-slate-800 flex flex-col items-center">
                  <div className={`w-10 h-10 ${tokens.badgeBg || 'bg-slate-100'} rounded-xl flex items-center justify-center mb-2`}>
                    <ClipboardList size={20} className={tokens.accentText} />
                  </div>
                  <h3 className="font-extrabold text-slate-900 dark:text-white text-[10px] tracking-widest mb-1">2 Minute Quiz</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-[10px]">Just 5 questions</p>
               </div>
               <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl text-center border border-slate-100 dark:border-slate-800 flex flex-col items-center">
                  <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl flex items-center justify-center mb-2">
                    <Brain size={20} className="text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="font-extrabold text-slate-900 dark:text-white text-[10px] tracking-widest mb-1">Smart Plan</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-[10px]">Tailored for you</p>
               </div>
            </div>

            <button 
              onClick={() => {
                StorageService.trackUserEvent({ action: 'diagnostic_started', category: 'onboarding', label: 'Intro' });
                setStep('registry');
              }}
              className={`w-full py-5 ${tokens.primaryBtn} rounded-2xl font-black tracking-widest flex items-center justify-center gap-3 transition-all transform active:scale-95 shadow-xl`}
            >
              Get Started Now
              <ArrowRight size={20} />
            </button>
            
            <button 
              onClick={() => {
                StorageService.trackUserEvent({ action: 'diagnostic_skipped', category: 'onboarding', label: 'Intro Skip' });
                onComplete({ weatherStatus: 'clouds', readinessScore: 0, skipped: true });
              }}
              className={`w-full py-2 text-slate-400 dark:text-slate-500 font-bold text-[10px] tracking-widest hover:${tokens.accentText} transition-colors`}
            >
              I'll do this later, skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'registry') {
    const registries = certName.toLowerCase().includes('cna') || certName.toLowerCase().includes('nurs') ? [
      { name: 'Credentia', desc: 'Registry Exam Vendor', icon: ShieldCheck },
      { name: 'Prometric', desc: 'Knowledge & Skills Test', icon: Target },
      { name: 'Headmaster', desc: 'State Board Exams', icon: Activity }
    ] : [
      { name: 'Standard', desc: 'General Registry Format', icon: ShieldCheck },
      { name: 'Clinical', desc: 'Hospital Specific Logic', icon: Target },
      { name: 'Advanced', desc: 'Critical Care Standards', icon: Activity }
    ];

    return (
      <div 
        className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-md flex justify-center items-start pt-4 md:pt-12 p-4 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="registry-choice-title"
      >
        <div className="max-w-xl w-full bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl p-10 border-2 border-slate-100 dark:border-slate-800 animate-in zoom-in-95 duration-500">
           <h2 id="registry-choice-title" className={`text-2xl ${tokens.headerFont || 'font-black'} text-slate-900 dark:text-white mb-2`}>Who is your exam with?</h2>
           <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-8">Knowing your exam company helps us pick the best questions for your practice.</p>
           
           <div className="grid gap-3">
              {registries.map((r) => (
                <button
                  key={r.name}
                  onClick={() => { setRegistry(r.name); setStep('quiz'); }}
                  className="group flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 rounded-2xl text-left hover:bg-slate-900 dark:hover:bg-slate-800 hover:border-slate-900 dark:hover:border-slate-700 transition-all active:scale-95"
                >
                  <div className="w-12 h-12 bg-white dark:bg-slate-900 rounded-xl flex items-center justify-center shadow-sm shrink-0">
                     <r.icon size={24} className={tokens.accentText} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-black text-slate-900 dark:text-white group-hover:text-white text-sm tracking-wide">{r.name}</h3>
                    <p className="text-slate-500 dark:text-slate-400 group-hover:text-white/70 text-[10px]">{r.desc}</p>
                  </div>
                  <ArrowRight size={18} className="text-slate-300 dark:text-slate-600 group-hover:text-white" />
                </button>
              ))}
           </div>
           
           <button 
             onClick={() => { setRegistry('General'); setStep('quiz'); }}
             className={`w-full mt-6 py-2 text-slate-400 dark:text-slate-500 font-black text-[9px] tracking-[0.2em] hover:${tokens.accentText}`}
           >
             Not sure? Use General Practice
           </button>
        </div>
      </div>
    );
  }


  if (step === 'quiz') {
    const q = questions[currentIndex];
    
    return (
      <div 
        className="fixed inset-0 z-[100] bg-slate-50 flex justify-center items-start pt-4 md:pt-12 p-2 md:p-4 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-question-text"
      >
        {isLoadingQuestions ? (
          <PageLoader compact message="Preparing Check" text={`Calibrating ${registry} Standards`} />
        ) : q ? (
          <div className="max-w-3xl w-full bg-white rounded-[2.5rem] shadow-2xl flex flex-col border border-slate-200 animate-in zoom-in-95 duration-500 overflow-hidden my-4">
            <div className="p-4 bg-slate-900 text-white flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                 <ShieldCheck aria-hidden="true" size={18} className="text-indigo-400" />
                 <span className="text-[10px] font-black tracking-widest">Exam Check: {registry}</span>
              </div>
              <div className="flex items-center gap-4">
                 <span className="text-[9px] font-black text-slate-400 tracking-widest">Question {currentIndex + 1} / {questions.length}</span>
                 <div className="flex gap-1" role="progressbar" aria-valuenow={currentIndex + 1} aria-valuemin={1} aria-valuemax={questions.length} aria-label="Assessment Progress">
                    {questions.map((_, idx) => (
                      <div key={idx} className={`w-3 h-1 rounded-full ${idx === currentIndex ? 'bg-indigo-400' : idx < currentIndex ? 'bg-emerald-400' : 'bg-white/20'}`} />
                    ))}
                 </div>
                 <button 
                   onClick={() => {
                     StorageService.trackUserEvent({ action: 'diagnostic_skipped', category: 'onboarding', label: 'Quiz Skip' });
                     onComplete({ weatherStatus: 'clouds', readinessScore: 45, skipped: true });
                   }}
                   className="px-3 py-1 bg-white/10 hover:bg-rose-600 hover:text-white text-slate-350 hover:scale-105 rounded-lg text-[8px] font-black tracking-widest uppercase transition-all"
                   title="Skip clinical readiness check and view study modules catalog"
                   id="skip-diagnostic-quiz-btn"
                 >
                   Skip Quiz
                 </button>
              </div>
            </div>
 
            <div className="p-6 md:p-12">
              <div className="max-w-2xl mx-auto" aria-live="polite">
                <span className="inline-block px-3 py-1 bg-slate-100 text-[9px] font-black text-slate-500 tracking-widest rounded mb-6">
                  {q.category}
                </span>
                <h2 id="quiz-question-text" className="text-xl md:text-2xl font-black text-slate-900 leading-snug mb-8">
                  {q.text}
                </h2>
 
                <div className="grid gap-3" role="radiogroup" aria-labelledby="quiz-question-text">
                  {q.options.map((opt, i) => {
                    const id = opt._id || i.toString();
                    const isSelected = q.type === 'ordering' 
                      ? currentSequence.includes(id)
                      : selectedOptionId === id;
                    
                    const seqIndex = q.type === 'ordering' ? currentSequence.indexOf(id) : -1;
 
                    return (
                      <button
                        key={id}
                        onClick={() => handleAnswer(id)}
                        disabled={isAnswered || (q.type === 'ordering' && isSelected)}
                        className={`w-full p-5 text-left border-2 transition-all duration-200 rounded-xl group ${
                          q.type === 'ordering'
                            ? (isSelected ? 'bg-indigo-50 border-indigo-500' : 'bg-white border-slate-200 hover:border-indigo-400')
                            : (selectedOptionId === id
                                ? (opt.isCorrect === true || String(opt.isCorrect).toLowerCase() === 'true' || Number(opt.isCorrect) === 1)
                                  ? 'bg-emerald-50 border-emerald-500'
                                  : 'bg-rose-50 border-rose-500'
                                : isAnswered && (opt.isCorrect === true || String(opt.isCorrect).toLowerCase() === 'true' || Number(opt.isCorrect) === 1)
                                  ? 'bg-emerald-50 border-emerald-500 border-dashed'
                                  : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 hover:border-indigo-600 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/80 hover:shadow-md font-bold')
                        }`}
                        role="radio"
                        aria-checked={isSelected}
                        aria-label={`Option ${String.fromCharCode(65 + i)}: ${opt.text}`}
                      >
                        <div className="flex items-start gap-4">
                           <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 font-black text-xs transition-colors ${
                             isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 
                             'bg-white border-slate-200 text-slate-400 group-hover:border-slate-400 group-hover:text-slate-900'
                           }`}>
                              {q.type === 'ordering' && isSelected ? (seqIndex + 1) : String.fromCharCode(65 + i)}
                           </div>
                           <span className="text-base font-bold text-slate-700 leading-tight pt-1">{opt.text}</span>
                           
                           {isAnswered && q.type !== 'ordering' && (opt.isCorrect === true || String(opt.isCorrect).toLowerCase() === 'true' || Number(opt.isCorrect) === 1) && (
                             <div className="ml-auto flex items-center gap-2">
                               <CheckCircle2 className="text-emerald-500" size={18} />
                               <span className="text-[10px] font-black text-emerald-600">Correct</span>
                             </div>
                           )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl p-8 border border-slate-200 text-center space-y-6 my-12 animate-in zoom-in-95 duration-500">
             <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mx-auto shadow-inner">
                <AlertTriangle size={32} />
             </div>
             <div className="space-y-2">
                <h3 className="text-2xl font-black text-slate-900 tracking-tight">Onboarding Diagnostic Offline</h3>
                <p className="text-xs text-slate-500 font-semibold leading-relaxed">
                   We are currently synchronizing your theoretical registry. Skip this initial quiz to instantly launch the full clinical prep catalogue.
                </p>
             </div>
             <button 
                onClick={() => { StorageService.trackUserEvent({ action: 'diagnostic_offline_skip', category: 'onboarding' }); onComplete({ weatherStatus: 'clouds', readinessScore: 50, skipped: true }); }}
                className="w-full py-4.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black tracking-widest text-[10px] uppercase shadow-lg shadow-indigo-100 transition-all active:scale-95"
             >
                Skip Directly To Dashboard
             </button>
          </div>
        )}
      </div>
    );
  }

  if (step === 'analyzing') {
    return (
      <div 
        className="fixed inset-0 z-[100] bg-indigo-950/90 backdrop-blur-lg flex flex-col items-center justify-center p-8 text-center"
        aria-live="assertive"
      >
        <PageLoader message="Analyzing Your Results" text="Our AI is checking your answers to build your personal study plan..." />
        
        <div className="mt-8 space-y-3 w-full max-w-xs">
          <div className="flex items-center justify-between text-[10px] font-black text-white/40 tracking-widest">
            <span>Pattern Extraction</span>
            <span className="text-indigo-400">COMPLETE</span>
          </div>
          <div 
            className="h-1 bg-white/10 rounded-full overflow-hidden"
            role="progressbar"
            aria-label="AI Analysis Progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={100}
          >
            <div className="h-full bg-indigo-500 animate-progress" style={{ width: '100%', transition: 'width 2s' }} />
          </div>
        </div>
      </div>
    );
  }

  if (step === 'results' && report) {
    const WeatherIcon = report.weatherStatus === 'sun' ? Sun : report.weatherStatus === 'clouds' ? Cloud : CloudLightning;
    const weatherColor = report.weatherStatus === 'sun' ? 'text-amber-500' : report.weatherStatus === 'clouds' ? 'text-slate-400' : 'text-indigo-500';

    return (
      <div 
        className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-md flex justify-center items-start pt-4 md:pt-12 p-4 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="diagnostic-results-title"
      >
        <div className="max-w-xl w-full bg-white rounded-[2.5rem] shadow-2xl my-8 overflow-hidden animate-in zoom-in-95 duration-500">
          <div className="relative p-8 border-b border-slate-100 text-center bg-slate-50">
             <div className="absolute top-0 right-0 p-6">
                <WeatherIcon aria-hidden="true" size={64} className={`${weatherColor} opacity-20`} />
             </div>
             
             <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-900 rounded-full mb-4">
                <Activity aria-hidden="true" size={12} className="text-indigo-400" />
                <span className="text-[9px] font-black text-white tracking-[0.2em]">Skill Profile Ready</span>
             </div>

             <h2 id="diagnostic-results-title" className="text-3xl font-black text-slate-900 mb-1">
                Score: <span className="text-indigo-600">{report.readinessScore}%</span>
             </h2>
             <p className="text-slate-400 font-bold tracking-widest text-[10px]">
                Your {certName} Baseline
             </p>
          </div>

          <div className="p-8 space-y-8">
             <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-base font-medium text-slate-700 leading-relaxed">
                  "{report.diagnosticAudit}"
                </p>
             </div>

             <div className="grid md:grid-cols-2 gap-8">
                <section aria-labelledby="strengths-title">
                  <h3 id="strengths-title" className="text-[10px] font-black text-slate-400 tracking-widest mb-4 flex items-center gap-2">
                    <TrendingUp aria-hidden="true" size={14} /> My Strengths
                  </h3>
                  <div className="grid gap-4">
                    {report.domainMastery.map((dm: any, i: number) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-bold text-slate-700">{dm.domain}</span>
                          <span className="text-[9px] font-black text-indigo-600">{dm.score}%</span>
                        </div>
                        <div 
                          className="h-2 bg-slate-100 rounded-full overflow-hidden"
                          role="progressbar"
                          aria-valuenow={dm.score}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={`${dm.domain} mastery level`}
                        >
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ${
                              dm.score > 80 ? 'bg-emerald-500' : dm.score > 60 ? 'bg-amber-500' : 'bg-rose-500'
                            }`}
                            style={{ width: `${dm.score}%` }} 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section aria-labelledby="next-steps-title">
                  <h3 id="next-steps-title" className="text-[10px] font-black text-slate-400 tracking-widest mb-4 flex items-center gap-2">
                    <ShieldCheck aria-hidden="true" size={14} /> Next Steps
                  </h3>
                  <div className="grid gap-2">
                    {report.learningPath.map((path: string, i: number) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-xl">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center text-indigo-600 text-[10px] font-black shadow-sm" aria-hidden="true">
                          {i + 1}
                        </div>
                        <p className="text-xs font-bold text-slate-700">{path}</p>
                      </div>
                    ))}
                  </div>
                </section>
             </div>

             <button 
                onClick={handleFinish}
                disabled={isSaving}
                className="w-full py-6 bg-slate-900 hover:bg-black text-white rounded-2xl font-black tracking-[0.25em] flex items-center justify-center gap-3 transition-all transform active:scale-95 shadow-xl disabled:opacity-70 disabled:cursor-not-allowed group/btn"
             >
                {isSaving ? (
                  <>
                    <Loader2 size={20} className="animate-spin text-indigo-400" />
                    Initializing Platform...
                  </>
                ) : (
                  <>
                    Initialize My Curriculum
                    <ArrowRight size={20} className="text-indigo-400 group-hover/btn:translate-x-1 transition-transform" />
                  </>
                )}
             </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
